import { ACTIONS, DEFAULT_KEYBINDS, MAX_BINDINGS_PER_ACTION } from "./schema";
import type { Action, Binding } from "./schema";

export type Keybinds = Record<Action, Binding[]>;
/** Read-only view accepted by the query helpers. */
export type KeybindsView = Readonly<Record<Action, readonly Binding[]>>;

/** Pressing this while capturing cancels instead of binding. It is never a valid binding. */
export const CANCEL_CODE = "Escape";
/** Reserved for the movement trace capture (game/trace.ts): never a valid binding, so it cannot collide with one. */
export const TRACE_CODE = "F8";

const MOUSE_RE = /^Mouse[0-4]$/;
/** KeyboardEvent.code values are PascalCase; requiring a capital also keeps `Object.prototype` names out. */
const CODE_RE = /^[A-Z][A-Za-z0-9]{1,24}$/;

/** True for strings the input layer can produce: a KeyboardEvent.code, MouseN (0..4) or a wheel direction. */
export function isValidBinding(b: unknown): b is Binding {
  if (typeof b !== "string") return false;
  if (b === CANCEL_CODE || b === TRACE_CODE || b === "Unidentified") return false;
  // Names reserved for pointer input must match exactly; they are not keyboard codes.
  if (b.startsWith("Mouse")) return isMouseBinding(b);
  if (b.startsWith("Wheel")) return isWheelBinding(b);
  return CODE_RE.test(b);
}

export function isMouseBinding(b: Binding): boolean {
  return MOUSE_RE.test(b);
}

export function isWheelBinding(b: Binding): boolean {
  return b === "WheelUp" || b === "WheelDown";
}

// ---- Capture ----

/** Binding for a keydown; null for Escape (cancel) and unidentified keys. */
export function bindingFromKeyboardEvent(e: { code: string }): Binding | null {
  return isValidBinding(e.code) ? e.code : null;
}

/** `button` is MouseEvent.button (0 left, 1 middle, 2 right, 3 back, 4 forward). */
export function bindingFromMouseButton(button: number): Binding | null {
  return Number.isInteger(button) && button >= 0 && button <= 4 ? `Mouse${button}` : null;
}

/** Negative deltaY scrolls up. */
export function bindingFromWheel(deltaY: number): Binding | null {
  if (deltaY < 0) return "WheelUp";
  if (deltaY > 0) return "WheelDown";
  return null;
}

// ---- Labels ----

const MOUSE_LABELS: Readonly<Record<string, string>> = {
  Mouse0: "Left Mouse",
  Mouse1: "Middle Mouse",
  Mouse2: "Right Mouse",
  Mouse3: "Mouse 4",
  Mouse4: "Mouse 5",
};

const CODE_LABELS: Readonly<Record<string, string>> = {
  Space: "Space",
  ShiftLeft: "Left Shift",
  ShiftRight: "Right Shift",
  ControlLeft: "Left Ctrl",
  ControlRight: "Right Ctrl",
  AltLeft: "Left Alt",
  AltRight: "Right Alt",
  MetaLeft: "Left Meta",
  MetaRight: "Right Meta",
  CapsLock: "Caps Lock",
  ArrowUp: "Up",
  ArrowDown: "Down",
  ArrowLeft: "Left",
  ArrowRight: "Right",
  Backquote: "`",
  Minus: "-",
  Equal: "=",
  BracketLeft: "[",
  BracketRight: "]",
  Backslash: "\\",
  Semicolon: ";",
  Quote: "'",
  Comma: ",",
  Period: ".",
  Slash: "/",
  IntlBackslash: "<",
  NumpadAdd: "Num +",
  NumpadSubtract: "Num -",
  NumpadMultiply: "Num *",
  NumpadDivide: "Num /",
  NumpadDecimal: "Num .",
  NumpadEnter: "Num Enter",
  PageUp: "Page Up",
  PageDown: "Page Down",
  WheelUp: "Wheel Up",
  WheelDown: "Wheel Down",
};

/** Short human label for a binding ("KeyW" -> "W", "Mouse2" -> "Right Mouse"). */
export function bindingLabel(b: Binding): string {
  const mouse = MOUSE_LABELS[b];
  if (mouse) return mouse;
  const named = CODE_LABELS[b];
  if (named) return named;
  let m = /^Key([A-Z])$/.exec(b);
  if (m?.[1]) return m[1];
  m = /^Digit([0-9])$/.exec(b);
  if (m?.[1]) return m[1];
  m = /^Numpad([0-9])$/.exec(b);
  if (m?.[1]) return `Num ${m[1]}`;
  // "ContextMenu" -> "Context Menu", "F12" stays.
  return b.replace(/([a-z])([A-Z])/g, "$1 $2");
}

// ---- Editing and conflicts ----

export function cloneKeybinds(k: KeybindsView): Keybinds {
  const out = {} as Keybinds;
  for (const a of ACTIONS) out[a] = [...k[a]];
  return out;
}

/** Other actions that already use `binding`. */
export function findConflicts(keybinds: KeybindsView, action: Action, binding: Binding): Action[] {
  const out: Action[] = [];
  for (const a of ACTIONS) {
    if (a !== action && keybinds[a].includes(binding)) out.push(a);
  }
  return out;
}

/** Every binding that is used by more than one action, with the actions sharing it. */
export function duplicateBindings(keybinds: KeybindsView): Map<Binding, Action[]> {
  const byBinding = new Map<Binding, Action[]>();
  for (const a of ACTIONS) {
    for (const b of keybinds[a]) {
      const list = byBinding.get(b);
      if (list) list.push(a);
      else byBinding.set(b, [a]);
    }
  }
  for (const [b, list] of byBinding) {
    if (list.length < 2) byBinding.delete(b);
  }
  return byBinding;
}

/**
 * Put `binding` in slot 0 or 1 of `action` (appends when the slot is past the end).
 * With `steal`, the binding is removed from every other action; otherwise duplicates are left
 * in place and reported through `findConflicts`. A binding already present in the other slot of
 * the same action is moved rather than duplicated.
 */
export function setBinding(
  keybinds: KeybindsView,
  action: Action,
  slot: number,
  binding: Binding,
  steal = false,
): Keybinds {
  const out = cloneKeybinds(keybinds);
  if (steal) {
    for (const a of ACTIONS) {
      if (a !== action) out[a] = out[a].filter((b) => b !== binding);
    }
  }
  const current = out[action];
  const target = Math.max(0, Math.min(slot, MAX_BINDINGS_PER_ACTION - 1, current.length));
  const next = current.slice();
  next[target] = binding;
  const seen = new Set<Binding>();
  out[action] = next.filter((b) => {
    if (seen.has(b)) return false;
    seen.add(b);
    return true;
  });
  return out;
}

/** Remove one slot of an action; the remaining binding moves up so slots stay contiguous. */
export function clearBinding(keybinds: KeybindsView, action: Action, slot: number): Keybinds {
  const out = cloneKeybinds(keybinds);
  out[action] = out[action].filter((_, i) => i !== slot);
  return out;
}

export function resetAction(keybinds: KeybindsView, action: Action): Keybinds {
  const out = cloneKeybinds(keybinds);
  out[action] = [...DEFAULT_KEYBINDS[action]];
  return out;
}

export function isActionDefault(keybinds: KeybindsView, action: Action): boolean {
  const cur = keybinds[action];
  const def = DEFAULT_KEYBINDS[action];
  return cur.length === def.length && cur.every((b, i) => b === def[i]);
}
